import clsx from "clsx";
import React, { FunctionComponent, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { CartPrintContactInfo } from "../blocks/Cart/PrintPage/CartPrintContactInfo";
import { CartPrintHeader } from "../blocks/Cart/PrintPage/CartPrintHeader";
import { CartPrintLegalInfo } from "../blocks/Cart/PrintPage/CartPrintLegalInfo";
import { CartPrintPage } from "../blocks/Cart/PrintPage/CartPrintPage";
import { CartPrintTotal } from "../blocks/Cart/PrintPage/CartPrintTotal";
import { useOrderDetails } from "../blocks/Cart/useOrderDetails";
import { cartActionPrint } from "../blocks/LeadForm/LeadFormModal";
import { Meta } from "../blocks/Meta/Meta";
import { NoData } from "../blocks/NoData/NoData";
import {
  DealerOrderPrintRoute,
  DealerWishlistPrintRoute,
} from "../navigation/DealerRoutes";
import { useSomeParams } from "../navigation/useSomeParams";
import {
  AnyOrderItem,
  CartState,
  VehicleInformation,
} from "../stores/slices/cartSlice";
import { LinearProgress } from "../ui/LinearProgress";

type VehicleKey = VehicleInformation | null;

const groupByVehicle = (cart?: CartState): [VehicleKey, AnyOrderItem[]][] => {
  if (!cart) return [];
  const map = new Map<VehicleKey, AnyOrderItem[]>();
  for (const order of cart.orderItemList) {
    const vehicleId = order.vehicleId;
    const vehicle =
      cart.vehicleInformation.find((item) => item.vehicleId === vehicleId) ||
      null;
    const list = map.get(vehicle) || [];
    map.set(vehicle, [...list, order]);
  }
  const result = [...map.entries()];
  return result;
};

export const DealerWishlistPrintPage: FunctionComponent = () => {
  const params = useSomeParams(DealerWishlistPrintRoute, DealerOrderPrintRoute);
  const { data: orderDetails, isLoading, isOrder } = useOrderDetails();
  const { t } = useTranslation();
  useEffect(() => {
    if (orderDetails && params?.action === cartActionPrint && !isLoading) {
      setTimeout(() => {
        window.print();
      }, 320);
    }
  }, [orderDetails?.orderNumber, params?.action, isLoading]);
  const entries = groupByVehicle(orderDetails);
  return (
    <>
      <Meta cmsTitle={null} title="dealerWishlistPrintPage" />
      <main
        className={clsx(
          "max-w-[8.5in] w-full mx-auto px-8 py-12 print:px-0 print:py-0 bg-white text-xs my-4 print:my-0",
          "shadow-xl print:shadow-none rounded-sm"
        )}
      >
        {isLoading && <LinearProgress className="h-0.5" />}
        {!isLoading && !orderDetails?.orderItemList.length && (
          <NoData>{t("cart.noOrders")}</NoData>
        )}
        {!isLoading &&
          orderDetails &&
          Boolean(orderDetails?.orderItemList.length) && (
            <>
              <CartPrintHeader isOrder={isOrder} orderDetails={orderDetails} />
              {entries.map(([vehicle, orders]) => (
                <CartPrintPage
                  key={vehicle?.vehicleId || "null"}
                  orders={orders}
                  vehicle={vehicle}
                />
              ))}
              <div className="my-8 print:my-0 print:mt-2 space-y-2">
                <CartPrintTotal orderDetails={orderDetails} />
                <CartPrintContactInfo orderDetails={orderDetails} />
                <CartPrintLegalInfo />
              </div>
              <div id="tw-print-ready" />
            </>
          )}
      </main>
    </>
  );
};
